import { TablePull } from 'features/tables/tablePull';
import React, { useEffect, useState } from 'react';
import { dataForDayOffTablesCancel, sortingDate } from 'shared/helpers/utils';
import { Badge, Popconfirm, Skeleton, Spin } from 'antd';
import { useNotifications } from 'shared/hooks/use-notifications';
import { TColumns, RequestsSearch } from 'shared/types/types';
import { usePullSort } from 'shared/hooks';
import { SortCondition } from 'shared/enums';
import { useIndicators } from 'features/indicators';
import { ONE_MINUTE } from 'shared/constants';
import { SearchKeysNews } from 'shared/enums/news';
import {
  DayOffsStatusesMap,
  DayOffsStatusTypesMap,
  DayOffTabs,
  TPreparedDayOffPublicated,
} from 'shared/types/dayoff.types';
import { useLazyDayoffsCanceledQuery, useTakeToWorkDayOffCanceledMutation } from 'app/services/edpApiV1/cancelDayoff';
import { useAppSelector } from 'app/store';
import { getAllAvailableOrganizations } from 'entities/organizations/model/organizations';
import { LoadingOutlined } from '@ant-design/icons';
import style from './index.module.scss';

export const DayOffPull = () => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [currentSize, setCurrentSize] = useState<number>(10);
  const [dataDayOffs, setDataDayOffs] = useState<TPreparedDayOffPublicated[] | []>([]);
  const [sortInfo, sortTrigger] = usePullSort();
  const sortOption = sortInfo.value && sortInfo.value === SortCondition.ASCEND ? SortCondition.ASC : SortCondition.DESC;
  const [trigger, result] = useTakeToWorkDayOffCanceledMutation();
  const [search, setSearch] = useState<RequestsSearch>({ byId: undefined, fio: undefined });
  const allAvailableOrganizations = useAppSelector(getAllAvailableOrganizations);
  const [currentTakeToWorkDayOff, setCurrentTakeToWorkDayOff] = useState<number[]>([]);

  const { isSuccess: successTakeToWork, isError: errorTakeToWork, reset: resetTakeToWork } = result;

  const handleToWork = async (id: number) => {
    setCurrentTakeToWorkDayOff([...currentTakeToWorkDayOff, id]);
    await trigger({ requestId: id });
    setCurrentTakeToWorkDayOff(currentTakeToWorkDayOff.filter(elem => elem === id));
  };

  const columns: TColumns = [
    {
      title: '№',
      dataIndex: 'id',
      key: 'id',
      width: 100,
    },
    {
      title: 'Дата',
      dataIndex: 'created',
      key: 'created',
      sortDirections: [SortCondition.ASCEND, SortCondition.DESCEND],
      sorter: (a, b) => sortingDate(a.created, b.created),
      onHeaderCell: () => ({
        onClick: () => sortTrigger(),
      }),
    },
    {
      title: 'ФИО',
      dataIndex: 'fio',
      key: 'fio',
    },

    {
      title: 'Дата начала',
      dataIndex: 'dayOffDate',
      key: 'dayOffDate',
    },
    {
      title: 'Дата окончания',
      dataIndex: 'endedAt',
      key: 'endedAt',
    },

    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <span>
          <Badge className={style.badge} status={DayOffsStatusTypesMap[status]} />
          {DayOffsStatusesMap[status]}
        </span>
      ),
    },
    {
      title: 'Действие',
      key: 'action',
      render: ({ id }) => (
        <>
          {currentTakeToWorkDayOff.includes(Number(id)) && result.isLoading ? (
            <Spin indicator={<LoadingOutlined spin />} />
          ) : (
            <Popconfirm title={'Вы уверены?'} onConfirm={() => handleToWork(id)}>
              <span className={style.changeResponsible}>Взять в работу</span>
            </Popconfirm>
          )}
        </>
      ),
    },
  ];

  // Добавляем пуллинг обновления списка и обновляем индикаторы через хук useIndicators
  const [getDayOffs, { data, isSuccess, isFetching, isLoading: loadingVacations }] = useLazyDayoffsCanceledQuery({
    pollingInterval: ONE_MINUTE,
  });

  useEffect(() => {
    getDayOffs({
      type: DayOffTabs.new,
      size: currentSize,
      page: currentPage,
      order: sortInfo.value ? sortOption : undefined,
      allAvailableOrganizations,
      ...search,
    });
  }, [currentPage, currentSize, getDayOffs, search, sortInfo.value, sortOption]);

  useEffect(() => {
    if (data) {
      const dataTable = dataForDayOffTablesCancel(data?.data || []);
      if (dataTable) {
        setDataDayOffs(dataTable);
      }
    }
  }, [data]);

  useEffect(() => {
    const paginationInfo = data?.pagination;
    if (dataDayOffs?.length === 0 && paginationInfo && currentPage !== 1) {
      // определяю текущую страницу
      const totalPages = Math.ceil(paginationInfo.total / paginationInfo.size);
      //  устанавливает текущую страницу на минимальное значение из текущей страницы и общего количества страниц,
      //  чтобы избежать установки текущей страницы на значение, превышающее общее количество страниц.
      setCurrentPage(Math.min(currentPage, totalPages));
    }
  }, [dataDayOffs, data]);

  useNotifications(successTakeToWork, errorTakeToWork, 'Взято в работу', 'Что-то пошло не так', resetTakeToWork);
  // подписываемся на обновление индикаторов, после обновления списка и отключаем пуллинг звпроса индикаторов
  useIndicators(isSuccess, isFetching, false);
  const onSearch = (data: string, key: SearchKeysNews) => {
    if (key === SearchKeysNews.SEARCH_ID) {
      setSearch({ fio: undefined, byId: data ? data : undefined });
    } else if (key === SearchKeysNews.SEARCH_NAME) {
      setSearch({ byId: undefined, fio: data ? data : undefined });
    } else {
      setSearch({ [key]: data ? data : undefined });
    }
  };
  return (
    <>
      {loadingVacations ? (
        <Skeleton active />
      ) : (
        <>
          {dataDayOffs && columns && (
            <TablePull
              dataSource={dataDayOffs}
              columns={columns}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              currentSize={currentSize}
              setCurrentSize={setCurrentSize}
              total={data?.pagination?.total}
              onSearch={onSearch}
            />
          )}
        </>
      )}
    </>
  );
};
